import { isReservedDomain } from "@common/helpers/reserved-sending-domain";
import {
    SendingDomainMethod,
    SendingDomainStatus,
} from "@common/types/domains/emailSending";

export type SuggestedCustomSenderKind = "gmail" | "dns";

export interface SuggestedCustomSender {
    kind: SuggestedCustomSenderKind;
    email: string;
    domain: string;
}

interface SenderCandidate {
    email: string;
    domain: string;
}

function emailHost(email: string): string | null {
    const separatorIndex = email.lastIndexOf("@");
    if (separatorIndex === -1) {
        return null;
    }
    const host = email
        .slice(separatorIndex + 1)
        .trim()
        .toLowerCase();
    return host.length > 0 ? host : null;
}

function resolveSenderCandidate({
    userEmail,
    sendingEmails,
}: {
    userEmail: string | null | undefined;
    sendingEmails: Array<{ email: string }>;
}): SenderCandidate | null {
    const email = userEmail?.trim().toLowerCase();
    if (!email) {
        return null;
    }

    const isEmailAlreadyConnected = sendingEmails.some(
        (sendingEmail) => sendingEmail.email.toLowerCase() === email,
    );
    if (isEmailAlreadyConnected) {
        return null;
    }

    const domain = emailHost(email);
    if (!domain) {
        return null;
    }

    return { email, domain };
}

export function getSuggestedGmailSender({
    userEmail,
    sendingEmails,
}: {
    userEmail: string | null | undefined;
    sendingEmails: Array<{ email: string }>;
}): SuggestedCustomSender | null {
    const candidate = resolveSenderCandidate({ userEmail, sendingEmails });
    if (!candidate) {
        return null;
    }

    return { kind: "gmail", email: candidate.email, domain: candidate.domain };
}

export function getSuggestedDnsSender({
    userEmail,
    sendingEmails,
    sendingDomains,
}: {
    userEmail: string | null | undefined;
    sendingEmails: Array<{ email: string }>;
    sendingDomains: Array<{
        domain: string;
        method: SendingDomainMethod;
        status: SendingDomainStatus;
    }>;
}): SuggestedCustomSender | null {
    const candidate = resolveSenderCandidate({ userEmail, sendingEmails });
    if (!candidate) {
        return null;
    }

    if (isReservedDomain(candidate.domain)) {
        return null;
    }

    const hasVerifiedDomain = sendingDomains.some(
        (sendingDomain) =>
            sendingDomain.method !== SendingDomainMethod.gmail &&
            sendingDomain.status === SendingDomainStatus.verified &&
            sendingDomain.domain.toLowerCase() === candidate.domain,
    );
    if (hasVerifiedDomain) {
        return null;
    }

    return { kind: "dns", email: candidate.email, domain: candidate.domain };
}
